/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   WorkspaceInventoryModal.tsx
 *     Feature         :   217-insights-dashboards (owner request 2026-07-14)
 *     Description     :   Detailed inventory of a single workspace — metadata, per-type
 *                         counts, and every Fabric item in it with as much collected detail
 *                         as we have (size, table/row counts, refresh status, language,
 *                         modified/created). Opened by clicking a row in the Workspace
 *                         Insights explorer. Fetches GET /api/workspace-insights/:id/inventory.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../services/apiClient';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Modal, DataTable, Badge, Select, Button, LoadingState, ErrorState, EmptyState, AppTooltip } from '../../components/ui';
import { UnmeasuredSizeTag } from '../../utils/unmeasuredSize';
import type { DataTableColumn, BadgeVariant } from '../../components/ui';
import { formatEastern } from '../../utils/dateFormatter';
import { AccessGate } from '../../components/access/AccessGate';
import { useHasAccess } from '../../hooks/useAccess';

interface InventoryItem {
  id: string;
  name: string;
  itemType: 'Lakehouse' | 'Warehouse' | 'DataPipeline' | 'Notebook' | 'SemanticModel' | 'Report';
  sizeGb: number | null;
  /** Set when the last collection did not size this lakehouse/warehouse: sizeGb is then its last known size. */
  sizeMeasurementError?: string | null;
  tableCount: number | null;
  rowCount: number | null;
  refreshStatus: string | null;
  storageMode: string | null;
  language: string | null;
  lastModified: string | null;
  reportType: string | null;
  linkedModel: string | null;
  endorsement: string | null;
  description: string | null;
  createdAt: string | null;
}

interface WorkspaceInventory {
  workspace: {
    workspaceId: string;
    name: string;
    workspaceType: string | null;
    capacityName: string | null;
    capacityType: string | null;
    clientId: string | null;
    clientName: string | null;
    isClientAssigned: boolean;
    createdAt: string | null;
    storageGb: number;
    /** Lakehouses and warehouses in storageGb the last collection did not size (counted at their last known size). */
    sizeNotMeasuredCount?: number;
    counts: {
      lakehouse: number; warehouse: number; pipeline: number;
      notebook: number; semanticModel: number; report: number; total: number;
    };
  };
  items: InventoryItem[];
}

const TYPE_META: Record<string, { label: string; variant: BadgeVariant }> = {
  Lakehouse: { label: 'Lakehouse', variant: 'info' },
  Warehouse: { label: 'Warehouse', variant: 'info' },
  DataPipeline: { label: 'Pipeline', variant: 'secondary' },
  Notebook: { label: 'Notebook', variant: 'secondary' },
  SemanticModel: { label: 'Semantic Model', variant: 'success' },
  Report: { label: 'Report', variant: 'warning' },
};

const fmtGb = (g: number | null): string => {
  if (g == null) return '—';
  if (g >= 1) return `${g.toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
  if (g > 0) return `${(g * 1024).toFixed(1)} MB`;
  return 'Empty';
};

/** Type-specific one-liner rolled up from whatever detail was collected. */
const detailOf = (it: InventoryItem): string => {
  const parts: string[] = [];
  if (it.tableCount != null) parts.push(`${it.tableCount.toLocaleString()} table${it.tableCount === 1 ? '' : 's'}`);
  if (it.rowCount != null) parts.push(`${it.rowCount.toLocaleString()} rows`);
  if (it.storageMode) parts.push(it.storageMode);
  if (it.refreshStatus) parts.push(`refresh: ${it.refreshStatus}`);
  if (it.language) parts.push(`lang: ${it.language}`);
  // Reports: type + the semantic model they're built on + endorsement.
  if (it.reportType) parts.push(it.reportType === 'PaginatedReport' ? 'Paginated report' : 'Power BI report');
  if (it.linkedModel) parts.push(`model: ${it.linkedModel}`);
  if (it.endorsement) parts.push(it.endorsement);
  return parts.join(' · ') || '—';
};

const Meta: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="min-w-0">
    <div className="text-xs text-[var(--color-text-secondary)]">{label}</div>
    <div className="truncate font-medium text-[var(--color-text-primary)]">{value}</div>
  </div>
);

export const WorkspaceInventoryModal: React.FC<{
  workspaceId: string | null;
  onClose: () => void;
  /** Ordered ids of the current (filtered) result list, for prev/next navigation. */
  workspaceIds?: string[];
  /** Navigate the modal to another workspace id. */
  onNavigate?: (id: string) => void;
}> = ({ workspaceId, onClose, workspaceIds, onNavigate }) => {
  const queryClient = useQueryClient();

  // Prev/next navigation across the result list. Freeze the id list when the modal
  // opens: a save/refetch that drops the current workspace out of the filtered parent
  // list (e.g. assigning a client while the "Shared" filter is on) must NOT yank the
  // Prev/Next controls mid-session.
  const [navIds, setNavIds] = useState<string[]>([]);
  useEffect(() => {
    if (!workspaceId) { setNavIds([]); return; }
    setNavIds((prev) => (prev.length ? prev : (workspaceIds ?? [])));
  }, [workspaceId, workspaceIds]);
  const ids = navIds.length ? navIds : (workspaceIds ?? []);
  const idx = workspaceId ? ids.indexOf(workspaceId) : -1;
  const hasNav = idx >= 0 && ids.length > 1 && !!onNavigate;
  const goPrev = React.useCallback(() => {
    if (hasNav && idx > 0) onNavigate!(ids[idx - 1]);
  }, [hasNav, idx, ids, onNavigate]);
  const goNext = React.useCallback(() => {
    if (hasNav && idx < ids.length - 1) onNavigate!(ids[idx + 1]);
  }, [hasNav, idx, ids, onNavigate]);

  // Arrow-key navigation (ignored while focused in a form control).
  useEffect(() => {
    if (!workspaceId || !hasNav) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [workspaceId, hasNav, goPrev, goNext]);

  const { data, isLoading, error, refetch } = useQuery<WorkspaceInventory>({
    queryKey: ['workspace-inventory', workspaceId],
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: WorkspaceInventory }>(
        `/api/workspace-insights/${workspaceId}/inventory`,
      );
      return res.data.data;
    },
    enabled: !!workspaceId,
    staleTime: 60_000,
  });

  // Client-assignment editor. Spec 285 (owner, 2026-10-01): assigning a workspace to a client is
  // SuperAdmin-only, like the rest of Clients — both endpoints refuse anyone else, so the editor
  // is not rendered and the client list is not fetched for them.
  const canAssignClients = useHasAccess('clients.client-list');
  const { data: clients } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['workspace-insights-clients'],
    enabled: canAssignClients,
    queryFn: async () => {
      const res = await apiClient.get<{ success: boolean; data: Array<{ id: string; name: string }> }>(
        '/api/workspace-insights/clients',
      );
      return res.data.data;
    },
    staleTime: 5 * 60_000,
  });

  const [selectedClient, setSelectedClient] = useState<string>('');
  const [saveError, setSaveError] = useState<string | null>(null);
  // Sync the dropdown to the loaded assignment (also after a save-refetch).
  useEffect(() => {
    setSelectedClient(data?.workspace.clientId ?? '');
  }, [data?.workspace.clientId, workspaceId]);

  const assignMutation = useMutation({
    mutationFn: async (clientId: string | null) => {
      const res = await apiClient.put<{ success: boolean; data: { clientId: string | null; clientName: string | null } }>(
        `/api/workspace-insights/${workspaceId}/client`,
        { clientId },
      );
      return res.data.data;
    },
    onSuccess: () => {
      setSaveError(null);
      void queryClient.invalidateQueries({ queryKey: ['workspace-inventory', workspaceId] });
      void queryClient.invalidateQueries({ queryKey: ['workspace-insights'] });
    },
    onError: (e: unknown) => {
      const err = e as { response?: { status?: number; data?: { error?: string } } };
      setSaveError(
        err.response?.status === 403
          ? 'Only a SuperAdmin can change workspace assignment.'
          : err.response?.data?.error ?? 'Failed to update assignment.',
      );
    },
  });

  // Clear the transient save UI ("Saved." / error) on OPEN and on NAVIGATION only —
  // not on save (so the "Saved." confirmation persists after a successful save, and
  // doesn't bleed onto the next workspace when the user steps Prev/Next).
  useEffect(() => {
    setSaveError(null);
    assignMutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const currentClientId = data?.workspace.clientId ?? '';
  const isDirty = selectedClient !== currentClientId;

  const columns: DataTableColumn<InventoryItem>[] = [
    {
      key: 'name', header: 'Item', width: '30%',
      render: (it) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-[var(--color-text-primary)]">{it.name}</div>
          {it.description && <div className="truncate text-xs text-[var(--color-text-secondary)]">{it.description}</div>}
        </div>
      ),
    },
    {
      key: 'itemType', header: 'Type', width: '14%',
      render: (it) => <Badge variant={TYPE_META[it.itemType]?.variant ?? 'secondary'}>{TYPE_META[it.itemType]?.label ?? it.itemType}</Badge>,
    },
    {
      key: 'sizeGb', header: 'Size', width: '11%', align: 'right',
      // Spec 286 T159 item 32: not sized by the last collection, the stored size is its last known one.
      render: (it) => (it.sizeMeasurementError ? (
        <AppTooltip title={`The last collection did not measure this item (${it.sizeMeasurementError}).${it.sizeGb ? ` Last known size: ${fmtGb(it.sizeGb)}.` : ''}`}>
          <span className="text-[var(--color-text-secondary)]">Not measured</span>
        </AppTooltip>
      ) : fmtGb(it.sizeGb)),
    },
    { key: 'detail', header: 'Details', width: '29%', render: (it) => <span className="text-[var(--color-text-secondary)]">{detailOf(it)}</span> },
    {
      key: 'when', header: 'Modified / Created', width: '16%',
      render: (it) => {
        const t = it.lastModified ?? it.createdAt;
        return t ? <span className="text-xs text-[var(--color-text-secondary)]">{formatEastern(t)}</span> : <span className="text-[var(--color-text-secondary)]">—</span>;
      },
    },
  ];

  const ws = data?.workspace;
  const chips: Array<[string, number]> = ws
    ? [
        ['Lakehouses', ws.counts.lakehouse],
        ['Warehouses', ws.counts.warehouse],
        ['Pipelines', ws.counts.pipeline],
        ['Notebooks', ws.counts.notebook],
        ['Semantic models', ws.counts.semanticModel],
        ['Reports', ws.counts.report],
      ]
    : [];

  return (
    <Modal
      isOpen={!!workspaceId}
      onClose={onClose}
      size="xl"
      title={ws ? `Workspace inventory — ${ws.name}` : 'Workspace inventory'}
    >
      {isLoading ? (
        <LoadingState rows={8} />
      ) : error ? (
        <ErrorState onRetry={() => refetch()} />
      ) : ws ? (
        <div className="space-y-4">
          {hasNav && (
            <div className="flex items-center justify-between">
              <Button variant="secondary" size="sm" onClick={goPrev} disabled={idx <= 0} aria-label="Previous workspace">
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <span className="text-xs text-[var(--color-text-secondary)]">{idx + 1} of {ids.length}</span>
              <Button variant="secondary" size="sm" onClick={goNext} disabled={idx >= ids.length - 1} aria-label="Next workspace">
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-4 text-sm md:grid-cols-3">
            <Meta label="Type" value={ws.workspaceType ?? '—'} />
            <Meta label="Capacity" value={ws.capacityName ? `${ws.capacityName}${ws.capacityType ? ` · ${ws.capacityType}` : ''}` : '—'} />
            <Meta label="Total items" value={ws.counts.total.toLocaleString()} />
            <Meta label="Storage footprint" value={<>{fmtGb(ws.storageGb)}<UnmeasuredSizeTag count={ws.sizeNotMeasuredCount} /></>} />
            <Meta label="First tracked" value={ws.createdAt ? formatEastern(ws.createdAt) : '—'} />
            <Meta label="Current assignment" value={ws.isClientAssigned ? (ws.clientName ?? 'Client-assigned') : 'Shared / central'} />
          </div>

          {/* Assign this workspace to a client (SuperAdmin — Spec 285). */}
          <AccessGate area="clients.client-list">
          <div className="flex flex-wrap items-end gap-3 rounded-[var(--radius-md)] border border-[var(--color-border)] p-4">
            <div className="w-64">
              <Select
                label="Assigned client"
                selectSize="sm"
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                disabled={assignMutation.isPending}
              >
                <option value="">Unassigned (shared / central)</option>
                {(clients ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Select>
            </div>
            <Button
              variant="primary"
              size="sm"
              disabled={!isDirty || assignMutation.isPending}
              onClick={() => assignMutation.mutate(selectedClient === '' ? null : selectedClient)}
            >
              {assignMutation.isPending ? 'Saving…' : 'Save assignment'}
            </Button>
            {saveError && <span className="pb-1.5 text-xs text-[var(--color-status-error)]">{saveError}</span>}
            {!saveError && assignMutation.isSuccess && !isDirty && (
              <span className="pb-1.5 text-xs text-[var(--color-status-success)]">Saved.</span>
            )}
          </div>
          </AccessGate>

          <div className="flex flex-wrap gap-2">
            {chips.filter(([, n]) => n > 0).map(([label, n]) => (
              <span key={label} className="inline-flex items-center gap-1 rounded-full border border-[var(--color-border)] px-2.5 py-1 text-xs text-[var(--color-text-secondary)]">
                <span className="font-semibold text-[var(--color-text-primary)]">{n}</span> {label}
              </span>
            ))}
          </div>

          {data && data.items.length === 0 ? (
            <EmptyState title="No items" message="This workspace has no collected Fabric items." />
          ) : (
            <DataTable
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(it) => it.id}
              pageSize={10}
              minWidth="46rem"
              emptyMessage="No items"
            />
          )}
        </div>
      ) : null}
    </Modal>
  );
};

export default WorkspaceInventoryModal;
